import {useEffect, useMemo, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {
  AUDIO_REDUCER, BOT_NAME,
  GENERAL_REDUCER,
  HIGHLIGHT_RANGE,
  USER_PROMPT_PREFIX, USER_PROMPT_SUFFIX
} from '../../redux/consts'
import {AUPRED_PROMPT_FUNC} from 'shared/common'
import styles from './SmartTextArea.module.scss'
import TextWithCursor from './TextWithCursor'
import {useDirection} from './useDirection'
import classes from 'classnames'

export default function ChatRenderer({ chat }) {

  const bottomRef = useRef(null)
  const lastAssistantIndex = useMemo(() => {
    return chat.reduce((lastIndex, { role }, index) => {
      return role === 'assistant' ? index : lastIndex;
    }, -1);
  }, [chat])

  const last = chat[chat.length - 1]
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'nearest', behavior: 'auto' })
  }, [chat.length, last?.text])
  const highlightRange = useSelector(state => state.getIn([AUDIO_REDUCER, HIGHLIGHT_RANGE])).toJS()

  // cursor should be shown only for the last assistant message
  return (
    <div className={styles.chatRenderer}>
      {chat.map(({role, text, topN, selectedTopNIndex, isTopNPanelOpen, startedAt}, index) =>
        <SingleChatParticle key={index}
                            text={text}
                            topN={topN}
                            role={role}
                            selectedTopNIndex={selectedTopNIndex}
                            isTopNPanelOpen={isTopNPanelOpen}
                            startedAt={startedAt}
                            isWithCursorAndTopN={index === lastAssistantIndex}
                            highlightRange={index === lastAssistantIndex ? highlightRange : null}
        />)}
      <div ref={bottomRef} />
    </div>
  )
}

function SingleChatParticle({ role, isWithCursorAndTopN, text, topN, selectedTopNIndex, isTopNPanelOpen, highlightRange, startedAt }) {
  const botName = useSelector(state => state.getIn([GENERAL_REDUCER, BOT_NAME]))
  const dir = useDirection(text)
  const [showTimestamp, setShowTimestamp] = useState(false)
  const [popupPosition, setPopupPosition] = useState(null)
  const popupRef = useRef(null)
  const avatarRef = useRef(null)

  const updatePopupPosition = () => {
    if (!avatarRef.current) return

    const rect = avatarRef.current.getBoundingClientRect()
    const gap = 8
    const top = rect.top + (rect.height / 2)
    const left = dir === 'rtl' ? rect.left - gap : rect.right + gap
    setPopupPosition({ top, left })
  }

  useEffect(() => {
    if (!showTimestamp) return

    const handleClickOutside = (e) => {
      if (avatarRef.current?.contains(e.target)) return
      if (popupRef.current?.contains(e.target)) return
      setShowTimestamp(false)
    }

    const handleViewportMove = () => {
      updatePopupPosition()
    }

    document.addEventListener('click', handleClickOutside)
    window.addEventListener('resize', handleViewportMove)
    // capture=true tracks nested scroll containers too.
    window.addEventListener('scroll', handleViewportMove, true)
    updatePopupPosition()

    return () => {
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('resize', handleViewportMove)
      window.removeEventListener('scroll', handleViewportMove, true)
    }
  }, [showTimestamp, dir])

  useEffect(() => {
    if (showTimestamp) {
      updatePopupPosition()
    }
  }, [showTimestamp, text])

  if (role === 'user'
    && text === USER_PROMPT_PREFIX+AUPRED_PROMPT_FUNC(botName)+USER_PROMPT_SUFFIX)
  {
    return null
  }

  const avatar =
    role === 'assistant'
      ? '🤖'
      : role === 'user'
        ? '🧑'
        : null

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Start time unavailable'
    const date = new Date(timestamp)
    if (Number.isNaN(date.getTime())) return 'Start time unavailable'

    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    })
  }

  const handleAvatarClick = (e) => {
    e.stopPropagation()
    const nextShow = !showTimestamp
    setShowTimestamp(nextShow)
    if (nextShow) updatePopupPosition()
  }

  const popupStyle = popupPosition
    ? { top: `${popupPosition.top}px`, left: `${popupPosition.left}px` }
    : { top: '-9999px', left: '-9999px' }

  return (
    <div
      className={classes(
        { [styles.particleContainerRtl]: dir === 'rtl' },
        { [styles.particleContainerLtr]: dir === 'ltr' },
        { [styles.assistantParticle]: role === 'assistant' }
      )}
    >
      <div className={styles.avatarContainer}>
        {avatar && (
          <span
            ref={avatarRef}
            className={styles.roleAvatar}
            onClick={handleAvatarClick}
            title="Click to see timestamp"
            style={{ cursor: 'pointer' }}
          >
            {avatar}
          </span>
        )}
      </div>

      {showTimestamp && (
        <div
          ref={popupRef}
          className={classes(
            styles.timestampPopup,
            { [styles.timestampPopupRtl]: dir === 'rtl' },
            { [styles.timestampPopupLtr]: dir !== 'rtl' }
          )}
          style={popupStyle}
        >
          {formatTime(startedAt)}
        </div>
      )}

      <div className={styles.particleTextWrap}>
        <TextWithCursor
          text={text}
          isWithCursorAndTopN={isWithCursorAndTopN}
          topN={topN}
          selectedTopNIndex={selectedTopNIndex}
          isTopNPanelOpen={isTopNPanelOpen}
          className={styles.charsContainer}
          highlightRange={highlightRange}
        />
      </div>
    </div>
  );
}
